
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { actionTypesModule, commonModule, companyModule, installationsModule, offersModule } from '@/store/modules/store-accessor'
import Dropdown from '../../../components/dropdown/Dropdown.vue'
import DropdownItem from '../../../components/dropdown/DropdownItem.vue'
import { TimeUtil } from '@/helpers/TimeUtil'
import { formatAmount, getOptimalTextColor, itemUnitAbbreviation, templatesFilenameLanguage, pagesExportLimit } from "@/helpers/data";
import FileSaver from 'file-saver';
import dayjs from 'dayjs'
import ExportMenu from "@/components/ExportMenu/ExportMenu.vue";
import PhotoCarouselViewer from '@/components/UI/PhotoCarouselViewer.vue'

@Component({
  components: { Dropdown, DropdownItem, ExportMenu, PhotoCarouselViewer }
})
export default class InstallationsSideviewOffersTab extends Vue {
  @Prop() installation: Record<string, any>;
  @Prop({ default: 0 }) offerId: number | string;

  tableheaders: any[] = [
    { class: 'font-weight-black', text: this.$t("offerNumber"), sortable: true, value: 'offer_number', width: '110px' },
    { class: 'font-weight-black', text: this.$t("title"), sortable: true, value: 'title', width: '32%' },
    { class: 'font-weight-black', text: this.$t("validUntil"), sortable: true, value: 'valid_until', width: '140px' },
    { class: 'font-weight-black', text: this.$t("total"), sortable: true, value: 'total_cost', width: '100px', align: 'center' },
    { class: 'font-weight-black', text: this.$t("status"), sortable: true, value: 'effective_status', width: '130px', align: 'center' },
    { class: 'font-weight-black', text: this.$t(""), sortable: false, value: 'photo_indicator', width: '80px', align: 'center' },
    { class: '', text: '', value: 'data-table-expand', width: '48px' },
  ]

  expandedItems: any[] = [];

  tableOptions = { itemsPerPage: 10, page: 1 };

  isDownloadingOfferPdf = false;
  isDownloadingExport = false;
  downloadingOfferPhotoId: string | null = null;
  updatingOfferStatusId: string | null = null;
  offerPhotoCounts: Record<string, number> = {};

  validOfferStatusTransitions: Record<string, string[]> = {
    DRAFT: ['SENT', 'ACCEPTED', 'REJECTED'],
    SENT: ['ACCEPTED', 'REJECTED'],
    ACCEPTED: [],
    REJECTED: [],
    EXPIRED: [],
  };

  async mounted() {
    await Promise.all([
      installationsModule.getInstallationOffers(this.installation.info.id),
      actionTypesModule.getActionTypesCombo(),
    ]);

    this.expandSelectedOffer();
    this.loadOfferPhotoCounts();
  }

  get offers() {
    return installationsModule.installationOffers;
  }

  get actionTypes() {
    return actionTypesModule.actionTypesCombo;
  }

  get currentOfferDetails() {
    return installationsModule.installationOfferDetails;
  }

  expandedOfferRequestId = 0;

  getExpandedOfferId(): string | null {
    return this.expandedItems[0]?.id || null;
  }

  refreshOfferExpansion(offerId: string | null | undefined) {
    if (!offerId) {
      return;
    }

    const refreshedOffer = this.offers.find((offer: OfferDto) => offer.id === offerId);

    if (!refreshedOffer) {
      this.closeAllOffersExpansions();
      return;
    }

    this.expandedItems = [refreshedOffer];
  }

  closeAllOffersExpansions() {
    this.expandedOfferRequestId++;
    this.expandedItems = [];
    installationsModule.setInstallationOfferDetails(null);
  }

  @Watch('offerId')
  onOfferIdChanged() {
    this.expandSelectedOffer();
  }

  expandSelectedOffer() {
    if (!this.offerId) {
      return;
    }

    const expansionOffer = this.offers.find((offer: OfferDto) => offer.id == this.offerId);

    if (!expansionOffer) {
      return;
    }

    const offerIndex = this.offers.findIndex((offer: OfferDto) => offer.id == this.offerId);
    const page = Math.floor(offerIndex / this.tableOptions.itemsPerPage) + 1;

    this.tableOptions.page = page;
    this.expandedItems = [expansionOffer];
  }

  @Watch('expandedItems')
  async onExpandedItemsChanged(newVal: any[]) {
    const requestId = ++this.expandedOfferRequestId;
    installationsModule.setInstallationOfferDetails(null);

    if (newVal.length === 0) {
      return;
    }

    const offer = newVal[0];

    if (!offer?.id) {
      return;
    }

    const offerDetails = await installationsModule.getInstallationOfferDetails({
      installationId: this.installation.info.id,
      offerId: offer.id,
    });

    if (requestId !== this.expandedOfferRequestId) {
      return;
    }

    installationsModule.setInstallationOfferDetails(offerDetails);
  }

  @Watch('currentOfferDetails')
  onCurrentOfferDetailsChanged(offer: OfferDetailsDto | null) {
    if (offer?.id) {
      this.$set(this.offerPhotoCounts, offer.id, offer.photos?.length || 0);
    }
  }

  async loadOfferPhotoCounts() {
    await Promise.all(this.offers.map(async (offer: OfferDto) => {
      try {
        const photos = await offersModule.getOfferPhotos({installationId: String(offer.installation_id), offerId: String(offer.id)});
        this.$set(this.offerPhotoCounts, offer.id, photos?.length || 0);
      } catch {
        this.$set(this.offerPhotoCounts, offer.id, 0);
      }
    }));
  }

  offerPhotoCount(offer: OfferDto): number {
    if (this.currentOfferDetails?.id === offer.id) {
      return this.currentOfferDetails.photos?.length || 0;
    }

    return this.offerPhotoCounts[offer.id] || 0;
  }

  addOffer() {
    commonModule.showModal({ name: 'add-offer-modal', payload: { installation: this.installation } })
  }

  editOffer(offer: OfferDto) {
    commonModule.showModal({ name: 'edit-offer-modal', payload: { ...offer } });
  }

  deleteOffer(offer: OfferDto) {
    commonModule.showModal({ name: 'delete-installation-offer-modal', payload: { offer: offer } });
  }

  async duplicateOffer(offer: OfferDto) {
    const offerDetails = this.currentOfferDetails?.id === offer.id
      ? this.currentOfferDetails
      : await installationsModule.getInstallationOfferDetails({ installationId: this.installation.info.id, offerId: offer.id });

    commonModule.showModal({
      name: 'add-offer-modal',
      payload: {
        installation: this.installation,
        duplicateOffer: offerDetails,
      },
    });
  }

  async addOfferLineItem(offer: OfferDto) {
    const offerDetails = this.currentOfferDetails?.id === offer.id
      ? this.currentOfferDetails
      : await installationsModule.getInstallationOfferDetails({ installationId: this.installation.info.id, offerId: offer.id });

    commonModule.showModal({
      name: 'add-offer-line-item-modal',
      payload: {
        offer: offerDetails,
      },
    });
  }

  addOfferPhotos(offer: OfferDto) {
    commonModule.showModal({ name: 'add-offer-photos-modal', payload: { offer } });
  }

  editOfferPhoto(offer: OfferDto, photo: OfferPhotoDto) {
    commonModule.showModal({ name: 'edit-offer-photo-modal', payload: { offer, photo } });
  }

  deleteOfferPhoto(offer: OfferDto, photo: OfferPhotoDto) {
    commonModule.showModal({ name: 'delete-offer-photo-modal', payload: { offer, photo } });
  }

  offerPhotos(offer: OfferDto): OfferPhotoDto[] {
    return this.currentOfferDetails?.id === offer.id ? this.currentOfferDetails.photos || [] : [];
  }

  photoPath(photo: OfferPhotoDto) {
    return `${photo.base_url.replace(/\/$/, '')}/${photo.src.replace(/^\//, '')}`;
  }

  formatPhotoDate(date: string): string {
    return TimeUtil.formatDateTime(date as any);
  }

  async downloadOfferPhoto(offer: OfferDto, photo: OfferPhotoDto) {
    this.downloadingOfferPhotoId = photo.id;
    try {
      let blob = await offersModule.getOfferPhoto({
        installationId: String(offer.installation_id),
        offerId: String(offer.id),
        photoId: String(photo.id),
      });
      const extension = photo.src.split('.').pop() || 'jpeg';
      const filename = `${this.installation.info.tag_id}_offer_${offer.offer_number}_${dayjs(photo.created_at).format('DD_MM_YYYY_HH_mm_ss')}.${extension}`;
      blob = new File([blob], filename);
      FileSaver.saveAs(blob);
    } finally {
      this.downloadingOfferPhotoId = null;
    }
  }

  validStatusTransitions(offer: OfferDto) {
    return this.validOfferStatusTransitions[offer.effective_status] || [];
  }

  async updateOfferStatus(offer: OfferDto, status: string) {
    this.updatingOfferStatusId = offer.id;

    try {
      await installationsModule.updateInstallationOffer({...offer, status });
    } finally {
      this.updatingOfferStatusId = null;
    }
  }

  editOfferLineItem(offer: OfferDetailsDto, lineItem: OfferLineItemDto) {
    const data = { offerId: offer.id, lineItemId: lineItem.id, installationId: offer.installation_id };

    commonModule.showModal({ name: 'edit-offer-line-item-modal', payload: { data: data } });
  }

  deleteOfferLineItem(offer: OfferDetailsDto, lineItem: OfferLineItemDto) {
    const data = { offerId: offer.id, lineItemId: lineItem.id, installationId: offer.installation_id };

    commonModule.showModal({ name: 'delete-offer-line-item-modal', payload: { data: data } });
  }

  async downloadOfferPdf(offer: OfferDto) {
    this.isDownloadingOfferPdf = true;
    try {
      const data = { offerId: offer.id, language: localStorage.getItem('lang'), user_timezone: dayjs.tz.guess() };

      const company = await companyModule.getCompanyInfo();
      const filename = `${templatesFilenameLanguage()}_${company.entity_name}_${this.installation.info.tag_id}_offer_${offer.offer_number}.pdf`.replace(/ /g, '_');
      const pdf = await offersModule.getOfferPdfExportAction(data);
      const file = new File([pdf], filename);
      FileSaver.saveAs(file);
    } finally {
      this.isDownloadingOfferPdf = false;
    }
  }

  downloadExportLengthCheck(type: string) {
    if (type === 'pdf') {
      return this.offers?.length > 0 && this.offers?.length <= pagesExportLimit();
    }
    return this.offers?.length > 0;
  }

  async donwloadOffersExport(format: string) {
    this.isDownloadingExport = true;
    const data = {
      user_timezone: dayjs.tz.guess(),
      language: localStorage.getItem('lang'),
      format,
      installation_id: this.installation.info.id,
    };

    try {
      const company = await companyModule.getCompanyInfo();
      const filetype = format === 'excel' ? 'xlsx' : 'pdf';
      const filename = `${templatesFilenameLanguage()}_${company.entity_name}_DfB_${data.installation_id}_offers_${dayjs().format('YYYY_MM_DD_HH_mm_ss')}.${filetype}`.replace(/ /g, '_');
      const exportFile = await installationsModule.getInstallationSideviewOffersExport(data);
      const file = new File([exportFile], filename);
      FileSaver.saveAs(file);
    } finally {
      this.isDownloadingExport = false;
    }
  }

  formatDate(date: string | null): string {
    return date ? TimeUtil.formatDate(date as any) : '-';
  }

  formatDateTime(date: string | null): string {
    return date ? TimeUtil.formatDateTime(date as any) : '-';
  }

  formatBalance(balance: number) {
    return formatAmount(balance);
  }

  formatCurrency(amount: number) {
    return `${formatAmount(amount)}€`;
  }

  offerSavings(offer: OfferDto) {
    return Math.max(Number(offer.subtotal || 0) - Number(offer.total_cost || 0), 0);
  }

  offerHasSavings(offer: OfferDto) {
    return this.offerSavings(offer) > 0;
  }

  offerForDisplay(offer: OfferDto): OfferDto {
    return this.currentOfferDetails?.id === offer.id ? this.currentOfferDetails : offer;
  }

  offerLineItems(offer: OfferDto): OfferLineItemDto[] {
    return this.currentOfferDetails?.id === offer.id ? this.currentOfferDetails.line_items : [];
  }

  lineItemsEmptyText(offer: OfferDto) {
    return this.currentOfferDetails?.id === offer.id ? this.$t("offerLineItemsEmpty") : this.$t("loadingOffersData");
  }

  lineTypeText(lineType: string) {
    switch (lineType) {
      case 'ACTION':
        return this.$t("action");
      case 'MATERIAL':
        return this.$t("material");
      case 'CUSTOM':
        return this.$t("offerOther");
      default:
        return '-';
    }
  }

  lineItemDescription(lineItem: OfferLineItemDto) {
    if (lineItem.line_type === 'ACTION') {
      return lineItem.action_type_text || lineItem.description;
    }

    return lineItem.description || (lineItem as any).inventory_item_text || (lineItem as any).inventory_item_name;
  }

  lineItemUnitText(lineItem: OfferLineItemDto) {
    if (lineItem.line_type === 'ACTION' && lineItem.time_unit) {
      return this.formatLineItemUnitInfo(lineItem, this.actionTimeUnitLabel(lineItem.time_unit, lineItem.quantity));
    }

    if (lineItem.line_type === 'MATERIAL' && lineItem.inventory_unit) {
      return this.formatMaterialUnitInfo(lineItem);
    }

    return '';
  }

  formatLineItemUnitInfo(lineItem: OfferLineItemDto, unitText: string) {
    return `(${lineItem.quantity} ${unitText} x ${lineItem.unit_cost}€)`;
  }

  formatMaterialUnitInfo(lineItem: OfferLineItemDto) {
    return `(${lineItem.quantity} ${itemUnitAbbreviation(lineItem.inventory_unit || undefined)} x ${lineItem.unit_cost}€)`;
  }

  actionTimeUnitLabel(unit: string, quantity: number): string {
    const normalizedUnit = unit.toUpperCase();

    if (normalizedUnit === 'HOUR') {
      return String(this.$i18n.t(Number(quantity) === 1 ? 'hourLowerCase' : 'hoursLowerCase'));
    }

    if (normalizedUnit === 'DAY') {
      return String(this.$i18n.t(Number(quantity) === 1 ? 'dayLowerCase' : 'daysLowerCase'));
    }

    return unit.toLowerCase();
  }

  lineItemChipColor(lineItem: OfferLineItemDto) {
    if (lineItem.line_type === 'ACTION') {
      const foundActionTypeColor = this.actionTypes.find((x: any) => x.id === lineItem.action_type_id)?.color;

      return foundActionTypeColor || 'primary';
    }

    if (lineItem.line_type === 'MATERIAL') {
      return 'materialChipColor';
    }

    if (lineItem.line_type === 'CUSTOM') {
      return 'customLineItemChipColor';
    }

    return 'grayBold';
  }

  lineItemChipTextColor(lineItem: OfferLineItemDto) {
    if (lineItem.line_type === 'ACTION') {
      const color = this.lineItemChipColor(lineItem);
      return this.isHexColor(color) ? getOptimalTextColor(color) : 'white';
    }

    return 'black';
  }

  isHexColor(color: string) {
    return /^#([0-9A-F]{3}){1,2}$/i.test(color);
  }

  shouldTruncateNotes(notes: string | null | undefined) {
    return !!notes && notes.length > 100;
  }

  truncatedNotes(notes: string | null | undefined) {
    return notes ? `${notes.slice(0, 100)}...` : '-';
  }

  notesText(notes: string | null | undefined) {
    return notes && notes.length > 0 ? notes : '-';
  }

  statusText(status: string) {
    return this.$i18n.t(status);
  }

  statusTransitionText(status: string) {
    switch (status) {
      case 'SENT':
        return this.$t('offerStatusSent');
      case 'ACCEPTED':
        return this.$t('acceptedAt');
      case 'REJECTED':
        return this.$t('rejectedAt');
      default:
        return this.statusText(status);
    }
  }

  statusTransitionIcon(status: string) {
    switch (status) {
      case 'SENT':
        return 'mdi-send';
      case 'ACCEPTED':
        return 'mdi-check';
      case 'REJECTED':
        return 'mdi-close';
      default:
        return 'mdi-circle-outline';
    }
  }

  statusColor(status: string) {
    switch (status) {
      case 'ACCEPTED':
        return 'darkGreen';
      case 'REJECTED':
      case 'EXPIRED':
        return 'error';
      case 'SENT':
        return 'primary';
      case 'DRAFT':
        return 'grayBold';
      default:
        return 'textColor2';
    }
  }

  clientName(client: ClientSnapshotDto) {
    return client.is_business ? `${client.entity_name} (${client.last_name} ${client.first_name})`.trim() : `${client.last_name} ${client.first_name}`.trim();
  }

  installationAddress(installation: InstallationSnapshotDto) {
    return [installation.address, installation.area, installation.city]?.filter(Boolean)?.join(', ');
  }

  installationText(installation: InstallationSnapshotDto) {
    return [installation.tag_id, installation.address, installation.city, installation.area, installation.zip_code].filter(Boolean).join(' | ');
  }

  offerIsExpired(offer: OfferDto) {
    return offer.valid_until ? dayjs(offer.valid_until).isBefore(dayjs(), 'day') : false;
  }

  get companyHasAdvancedPlan() {
    return companyModule.company?.modules?.includes('DFB_ADVANCED_PLAN');
  }

  disableAction(offer: OfferDetailsDto) {
    return offer.effective_status !== 'DRAFT';
  }

};
